import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, TextField, Select, MenuItem, Checkbox, FormControlLabel, Typography, Button } from '@mui/material';

// SVG field (#197 follow-up): the raw source is show/hide, collapsed by
// default. The preview always renders (when there's a value) so the field
// reads as "an image"; the source textarea only appears once toggled on —
// useful for viewing without cluttering the form with raw markup, and for
// editing when it's actually needed.
function SvgImageField({ label, required, value, onChange, testId, svgSrc }) {
  const [showSource, setShowSource] = useState(!value);
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: svgSrc ? 1 : 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}{required ? ' *' : ''}</Typography>
        <Button size="small" onClick={() => setShowSource((s) => !s)} data-testid={`${testId}-toggle-source`}>
          {showSource ? 'Hide source' : 'Show source'}
        </Button>
      </Box>
      {showSource && (
        <TextField
          label={`${label} (SVG source)`} required={required} fullWidth multiline minRows={4}
          value={value || ''} onChange={(e) => onChange(e.target.value)}
          slotProps={{ htmlInput: { 'data-testid': testId } }}
        />
      )}
      {svgSrc && (
        <Box sx={{ mt: 1, p: 1, border: '1px solid', borderColor: 'divider', display: 'inline-block' }}>
          <img src={svgSrc} alt={`${label} preview`} data-testid={`${testId}-preview`}
            style={{ maxWidth: 200, maxHeight: 200 }} />
        </Box>
      )}
    </Box>
  );
}

SvgImageField.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  testId: PropTypes.string.isRequired,
  svgSrc: PropTypes.string,
};

/**
 * Renders one typed meta-model field (#196's item_type_fields, #197's ask) as
 * the appropriate input widget. `field` is an item_type_fields row: {key, label,
 * data_type, required, options}. `value`/`onChange(newValue)` hold the raw
 * string/array value as stored in the item's attributes.
 */
export default function FieldRenderer({ field, value, onChange }) {
  const { data_type: dataType, label, required } = field;
  const options = Array.isArray(field.options) ? field.options
    : (typeof field.options === 'string' && field.options ? JSON.parse(field.options) : []);
  const testId = `field-${field.key}`;

  switch (dataType) {
    case 'textarea':
      return (
        <TextField
          label={label} required={required} fullWidth multiline minRows={3}
          value={value || ''} onChange={(e) => onChange(e.target.value)}
          slotProps={{ htmlInput: { 'data-testid': testId } }}
        />
      );

    case 'number':
      return (
        <TextField
          label={label} required={required} type="number" fullWidth
          value={value ?? ''} onChange={(e) => onChange(e.target.value)}
          slotProps={{ htmlInput: { 'data-testid': testId } }}
        />
      );

    case 'boolean':
      return (
        <FormControlLabel
          control={(
            <Checkbox checked={!!value} onChange={(e) => onChange(e.target.checked)}
              data-testid={testId} />
          )}
          label={label}
        />
      );

    case 'date':
      return (
        <TextField
          label={label} required={required} type="date" fullWidth
          value={value || ''} onChange={(e) => onChange(e.target.value)}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': testId } }}
        />
      );

    case 'enum':
      return (
        <TextField
          select label={label} required={required} fullWidth
          value={value || ''} onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        >
          {options.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
        </TextField>
      );

    case 'multiselect': {
      // `value` arrives as a real array once edited in this session, but on
      // initial load it's whatever `attributesToRows` stored — which
      // JSON-stringifies array attribute values (typeof array === 'object'),
      // e.g. '["Red","Blue"]'. A naive comma-split on that string reproduces
      // the raw JSON as garbage options ('["Red"', '"Blue"]'). Parse JSON
      // first; only fall back to comma-splitting for a legacy plain
      // comma-separated string (never JSON-encoded).
      let selected = [];
      if (Array.isArray(value)) {
        selected = value;
      } else if (value) {
        try {
          const parsed = JSON.parse(value);
          selected = Array.isArray(parsed) ? parsed : [String(parsed)];
        } catch {
          selected = String(value).split(',').map((s) => s.trim()).filter(Boolean);
        }
      }
      return (
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}{required ? ' *' : ''}</Typography>
          <Select
            multiple fullWidth value={selected}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            renderValue={(sel) => sel.join(', ')}
          >
            {options.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
          </Select>
        </Box>
      );
    }

    case 'svg_image': {
      // Edit the raw SVG markup; preview via an <img> data-URI — NOT
      // dangerouslySetInnerHTML/inline DOM injection, which would let an
      // embedded <script> execute. An <img> src never executes SVG scripts.
      const svgSrc = value ? `data:image/svg+xml;utf8,${encodeURIComponent(value)}` : null;
      // #197: the raw source is a show/hide option — collapsed by default so
      // the field reads as "an image" rather than "a textarea of markup";
      // toggle to reveal the editable source when it actually needs editing.
      return <SvgImageField label={label} required={required} value={value} onChange={onChange} testId={testId} svgSrc={svgSrc} />;
    }

    case 'reference':
      return (
        <TextField
          label={`${label} (item id)`} required={required} fullWidth
          value={value || ''} onChange={(e) => onChange(e.target.value)}
          slotProps={{ htmlInput: { 'data-testid': testId } }}
        />
      );

    case 'text':
    default:
      return (
        <TextField
          label={label} required={required} fullWidth
          value={value || ''} onChange={(e) => onChange(e.target.value)}
          slotProps={{ htmlInput: { 'data-testid': testId } }}
        />
      );
  }
}

FieldRenderer.propTypes = {
  field: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    data_type: PropTypes.string.isRequired,
    required: PropTypes.bool,
    options: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  }).isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
};
